/*
 * Google Consent Mode v2 signals, europe only — shared by the Ads tag (googleAds.js) and the Tag
 * Manager container (googleTagManager.js).
 *
 * Google reads the consent state from the dataLayer, and the "default" command has to be on the
 * queue before any Google tag is configured: a tag that runs first measures with no consent state
 * at all. For EEA traffic that is not a neutral outcome — since March 2024 Google Ads will not use
 * a visitor for conversions or audiences unless ad_user_data and ad_personalization are signalled,
 * so a tag without them loads, fires, and still shows nothing in the dashboard.
 *
 * Owning the signals here, rather than in each tag module, is what guarantees the order: this runs
 * first from index.js, pushes the default straight away (all denied until the banner is answered),
 * and relays every later decision as an update. Pushing onto the dataLayer loads nothing and sets
 * no cookie, so doing it before consent is fine.
 */

import { IS_EUROPE } from '../config/region';
import { getConsent, onConsentChange } from './consent';

/* gtag.js reads the arguments object off the queue positionally, so this cannot be rewritten as
   (...args) => dataLayer.push(args). Assigned to window too, which is where gtag.js looks. */
export function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

const state = (granted) => (granted ? 'granted' : 'denied');

const signals = (consent) => ({
  analytics_storage: state(consent.analytics),
  ad_storage: state(consent.marketing),
  ad_user_data: state(consent.marketing),
  ad_personalization: state(consent.marketing),
  functionality_storage: state(consent.functional),
  personalization_storage: state(consent.functional),
  /* Not a choice — it is what lets a tag remember an error or a spam signal. */
  security_storage: 'granted',
});

export const initGoogleConsent = () => {
  if (!IS_EUROPE) return undefined;
  window.gtag = gtag;
  gtag('consent', 'default', signals(getConsent()));
  return onConsentChange((consent) => gtag('consent', 'update', signals(consent)));
};
